﻿/* eslint-disable @typescript-eslint/naming-convention -- names are given by the auto-generated sdk */
// noinspection JSUnusedGlobalSymbols

import * as Sentry from '@sentry/react';
import {
  AiPrompts,
  ApplicationConfigurations,
  ApplicationData,
  Aso,
  Brands,
  Calculations,
  Categories,
  CategoryConfigurationDefinitions,
  ConfigurationFieldSpecifications,
  Departments,
  EntityLists,
  Hints,
  HttpClient,
  Notifications,
  ProductLists,
  Products,
  Regions,
  Settings,
  SkuClasses,
  SkuDescriptions,
  Scopes,
  Skus,
  StoreSchedules,
  Stores,
  Templates,
  Triggers,
  Warehouses,
  WarehouseTypes,
  Ybot,
} from '@ydistri/api-sdk';
import Auth from '../auth/Auth';
import { getApiUrl } from '../helpers';
import { runInterceptor } from '@ydistri/utils';
import {
  getItem,
  LSKey,
  removeRetiredItems,
  setSelectedTemplateForProject,
} from '../store/localStore';
import { setSelectedProjectApiContext } from '../apis/apiHeader';

export const API = new HttpClient({
  baseURL: getApiUrl(),
});

API.instance.interceptors.request.use(runInterceptor, error => {
  // eslint-disable-next-line no-console,@typescript-eslint/no-unsafe-member-access -- we want to output the error
  console.log(error.response);
  Sentry.captureException(error);

  if (error instanceof Error) {
    return Promise.reject(error);
  } else {
    const errorMessage = String(error);
    return Promise.reject(new Error(errorMessage));
  }
});

API.instance.interceptors.response.use(
  req => req,
  async error => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- error is any rom the axios response
    if (error?.response?.status === 401) {
      const originalRequest =
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- axios config extension for retry marker
        error.config as // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents -- overriden because we need to deploy
          | ({ _ydistriRetry?: boolean; headers?: Record<string, string> } & typeof error.config)
          | undefined;

      if (originalRequest && !originalRequest._ydistriRetry) {
        originalRequest._ydistriRetry = true;

        const auth = new Auth();
        try {
          const refreshed = await auth.refreshSession();
          if (refreshed) {
            const bearer = auth.getBearer();
            if (bearer) {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- override
              originalRequest.headers = {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                ...originalRequest.headers,
                Authorization: bearer,
              };
            }

            return API.instance.request(originalRequest);
          }
        } catch {
          // Fall through to logout when refresh fails.
        }
      }

      new Auth().signOut();
      // eslint-disable-next-line no-console -- we want to output
      console.log('LOGOUT - INTERCEPTOR');
    }

    if (error instanceof Error) {
      return Promise.reject(error);
    } else {
      const errorMessage = String(error);
      return Promise.reject(new Error(errorMessage));
    }
  },
);

export let urlProjectShortName: undefined | string;
export let urlSelectedTemplateId: undefined | number;
export const pathSplit = window.location.pathname.split('/');

removeRetiredItems();

// 'mcp' is defensive only - an unknown slug already selects nothing - it just says out loud that
// /mcp/authorize/... (the MCP approval page, outside the project routes) is not a project.
if (pathSplit.length > 1 && !['login', 'register', 'account', 'mcp'].includes(pathSplit[1])) {
  // Only accept a slug of a project we know about; an unknown slug in the URL selects nothing here
  // and ProjectRoute redirects to the first available project once projects are loaded.
  const projectFromUrl = getItem(LSKey.PROJECTS_BY_SHORT_NAME)?.[pathSplit[1]];
  if (projectFromUrl) {
    urlProjectShortName = projectFromUrl.shortName;
    // Seeds this tab's project identity before anything can read it - the URL is authoritative here
    setSelectedProjectApiContext(API, urlProjectShortName);
  }
}

if (pathSplit.length > 3 && pathSplit[2] === 'configuration') {
  urlSelectedTemplateId = parseInt(pathSplit[3]);
  if (urlSelectedTemplateId && urlProjectShortName) {
    setSelectedTemplateForProject(urlProjectShortName, urlSelectedTemplateId);
  }
}

export const AiPromptsCollection = new AiPrompts(API);
export const ApplicationConfigurationsCollection = new ApplicationConfigurations(API);
export const ApplicationDataCollection = new ApplicationData(API);
export const CalculationsCollection = new Calculations(API);
export const CategoriesCollection = new Categories(API);
export const CategoryConfigurationDefinitionsCollection = new CategoryConfigurationDefinitions(API);
export const ConfigurationFieldSpecificationsCollection = new ConfigurationFieldSpecifications(API);
export const DepartmentsCollection = new Departments(API);
export const HintsCollection = new Hints(API);
export const NotificationsCollection = new Notifications(API);
export const ProductsCollection = new Products(API);
export const ProductListsCollection = new ProductLists(API);
export const RegionsCollection = new Regions(API);
export const SettingsCollection = new Settings(API);
export const SkuClassesCollection = new SkuClasses(API);
export const SkusCollection = new Skus(API);
export const TemplatesCollection = new Templates(API);
export const TriggersCollection = new Triggers(API);
export const StoresCollection = new Warehouses(API);
export const WarehouseTypesCollection = new WarehouseTypes(API);
export const BrandsCollection = new Brands(API);
export const EntityListsCollection = new EntityLists(API);
export const Stores2Collection = new Stores(API);
export const AsoCollection = new Aso(API);
export const SkuDescriptionsCollection = new SkuDescriptions(API);
export const StoreSchedulesCollection = new StoreSchedules(API);
export const ScopesCollection = new Scopes(API);
export const YbotCollection = new Ybot(API);
